import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import AuthContext from '../../context/authContext';
import useOutage from '../../hooks/useOutage';
import useIssue from '../../hooks/useIssue';
import useEntertainment from '../../hooks/useEntertainment';
import { ISSUE_PRIORITY_BY_KEY } from '../../util/issueConstants';

const DATE_FORMAT = 'DD.MM.YYYY HH:mm';

// Collect all resource ids of an entertainment (plain list + ordered resources), deduplicated.
const entertainmentResourceIds = e => [
  ...new Set([
    ...(e.resourceIds || []),
    ...((e.orderedResources?.resources || []).map(r => r.resourceId) || []),
  ]),
];

const branchLabel = branch => branch?.longName || branch?.locationName || '';

export default function OutageBoardView() {
  const authCtx = useContext(AuthContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const { getOutages, createOutage, endOutage, updateVestCount } = useOutage();
  const { getStatusOverview } = useIssue();
  const { getEntertainments } = useEntertainment();

  const canRead = authCtx.checkRight('outage:read') || authCtx.checkRight('outage:read:own');
  const canManage = authCtx.checkRight('outage:manage');

  const [overview, setOverview] = useState(null);
  const [outages, setOutages] = useState([]);
  // lt* vest fields per entertainment id (loaded from the full entertainment docs).
  const [entDetails, setEntDetails] = useState({});
  const [branchFilter, setBranchFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Create-outage dialog target: { level, branchId, entertainmentId, resourceId?, name }.
  const [outageTarget, setOutageTarget] = useState(null);
  const reasonRef = useRef(null);

  // End-outage confirmation target: { outageId, name }.
  const [endTarget, setEndTarget] = useState(null);

  // Vest settings dialog target entertainment (from the overview list).
  const [vestDialogEnt, setVestDialogEnt] = useState(null);
  const vestTotalRef = useRef(null);
  const vestRealCapacityRef = useRef(null);

  const loadAll = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const [overviewRes, outagesRes, entRes] = await Promise.all([
          getStatusOverview(),
          getOutages(),
          getEntertainments().catch(() => null),
        ]);
        if (overviewRes) setOverview(overviewRes);
        if (outagesRes?.outages) setOutages(outagesRes.outages);
        const entList = Array.isArray(entRes) ? entRes : entRes?.entertainments;
        if (Array.isArray(entList)) {
          setEntDetails(
            Object.fromEntries(
              entList.map(e => [
                e._id,
                {
                  ltSlotCapacity: e.ltSlotCapacity,
                  ltSlotRealCapacity: e.ltSlotRealCapacity,
                  ltVestCountTotal: e.ltVestCountTotal,
                  ltVestCountDefective: e.ltVestCountDefective,
                },
              ])
            )
          );
        }
      } catch {
        // Error toasts are emitted by the hooks.
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [getStatusOverview, getOutages, getEntertainments]
  );

  useEffect(() => {
    if (!canRead) return undefined;
    loadAll();
    const interval = setInterval(() => loadAll(true), 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  const branches = useMemo(() => overview?.branches || [], [overview]);

  // Default to the only branch when there is exactly one.
  useEffect(() => {
    if (branches.length === 1) setBranchFilter(branches[0]._id);
  }, [branches]);

  const resourceById = useMemo(
    () => Object.fromEntries((overview?.resources || []).map(r => [r._id, r])),
    [overview]
  );

  // Active outages of the overview, indexed by their target for the board switches.
  // Resource-level outages are keyed by entertainment *and* resource: the same
  // resource can be assigned to several entertainments, but an outage reported
  // on one entertainment must only mark that one as not bespielbar — not every
  // entertainment that happens to share the resource.
  const { entOutageByEnt, resOutageByEntRes } = useMemo(() => {
    const byEnt = {};
    const byEntRes = {};
    (overview?.activeOutages || []).forEach(o => {
      if (o.level === 'entertainment' && o.entertainmentId) byEnt[o.entertainmentId] = o;
      else if (o.resourceId && o.entertainmentId)
        byEntRes[`${o.entertainmentId}_${o.resourceId}`] = o;
    });
    return { entOutageByEnt: byEnt, resOutageByEntRes: byEntRes };
  }, [overview]);

  const visibleEntertainments = useMemo(
    () =>
      (overview?.entertainments || [])
        .filter(e => e.active !== false)
        .filter(e => branchFilter === 'all' || e.branch === branchFilter)
        .sort((a, b) =>
          (a.entertainmentName || a.longName || '').localeCompare(
            b.entertainmentName || b.longName || ''
          )
        ),
    [overview, branchFilter]
  );

  // The active/recently-ended table follows the same Standort filter as the
  // cards, so switching to one location does not still list outages (including
  // ended ones for the same shared resource) from other locations.
  const sortedOutages = useMemo(
    () =>
      outages
        .filter(
          o =>
            branchFilter === 'all' ||
            String(o.branchId?._id || o.branchId) === String(branchFilter)
        )
        .sort((a, b) => {
          if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
          return dayjs(b.startedAt).valueOf() - dayjs(a.startedAt).valueOf();
        }),
    [outages, branchFilter]
  );

  const openOutageDialog = target => {
    setOutageTarget(target);
  };

  const handleCreateOutage = async () => {
    if (!outageTarget) return;
    setBusy(true);
    try {
      await createOutage({
        level: outageTarget.level,
        branchId: outageTarget.branchId,
        entertainmentId: outageTarget.entertainmentId,
        ...(outageTarget.resourceId ? { resourceId: outageTarget.resourceId } : {}),
        ...(reasonRef.current?.value?.trim() ? { reason: reasonRef.current.value.trim() } : {}),
      });
      toast.success('Ausfall gemeldet – Ticket & Blocker erstellt');
      setOutageTarget(null);
      loadAll(true);
    } catch (err) {
      if (err?.code === 'OUTAGE_ALREADY_ACTIVE') {
        toast.error('Für dieses Ziel ist bereits ein Ausfall aktiv');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleEndOutage = async () => {
    if (!endTarget) return;
    setBusy(true);
    try {
      await endOutage(endTarget.outageId);
      toast.success('Ausfall beendet – Blocker entfernt & Ticket gelöst');
      setEndTarget(null);
      loadAll(true);
    } catch {
      // Error toast is emitted by the hook.
    } finally {
      setBusy(false);
    }
  };

  // Merge lt* fields returned by the backend into the local detail cache.
  const mergeEntDetail = (entertainmentId, entertainment) => {
    setEntDetails(prev => ({
      ...prev,
      [entertainmentId]: { ...prev[entertainmentId], ...entertainment },
    }));
  };

  // Change the defective vest count by +/-1 with an optimistic UI update.
  const handleDefectiveChange = async (entertainmentId, delta) => {
    const detail = entDetails[entertainmentId] || {};
    const total = detail.ltVestCountTotal;
    const current = detail.ltVestCountDefective || 0;
    const next = current + delta;
    if (next < 0 || (total != null && next > total)) return;
    mergeEntDetail(entertainmentId, { ltVestCountDefective: next });
    try {
      const res = await updateVestCount({
        entertainmentId,
        defectiveVestCount: next,
        ...(total != null ? { totalVestCount: total } : {}),
      });
      if (res?.entertainment) mergeEntDetail(entertainmentId, res.entertainment);
    } catch {
      // Roll the optimistic update back; the hook already toasts the error.
      mergeEntDetail(entertainmentId, { ltVestCountDefective: current });
    }
  };

  const handleVestSettingsSave = async () => {
    if (!vestDialogEnt) return;
    const detail = entDetails[vestDialogEnt._id] || {};
    const totalRaw = vestTotalRef.current?.value;
    const realRaw = vestRealCapacityRef.current?.value;
    const payload = { entertainmentId: vestDialogEnt._id };
    if (totalRaw !== undefined && totalRaw !== '') payload.totalVestCount = Number(totalRaw);
    if (detail.ltVestCountTotal == null && realRaw !== undefined && realRaw !== '') {
      payload.realCapacity = Number(realRaw);
    }
    if (payload.totalVestCount === undefined && payload.realCapacity === undefined) {
      setVestDialogEnt(null);
      return;
    }
    setBusy(true);
    try {
      const res = await updateVestCount(payload);
      if (res?.entertainment) mergeEntDetail(vestDialogEnt._id, res.entertainment);
      toast.success('Westen-Einstellungen gespeichert');
      setVestDialogEnt(null);
    } catch {
      // Error toast is emitted by the hook.
    } finally {
      setBusy(false);
    }
  };

  if (!canRead) return <Navigate to='/' replace />;

  const availabilitySwitch = (outage, onReport, onEnd, small = false) => (
    <Switch
      size={small ? 'small' : 'medium'}
      checked={!outage}
      color='success'
      disabled={!canManage || busy}
      onChange={() => (outage ? onEnd() : onReport())}
      sx={
        outage
          ? {
              '& .MuiSwitch-switchBase .MuiSwitch-thumb': {
                backgroundColor: theme.palette.error.main,
              },
              '& .MuiSwitch-track': {
                backgroundColor: `${theme.palette.error.main} !important`,
                opacity: '0.5 !important',
              },
            }
          : undefined
      }
    />
  );

  const renderVestBox = ent => {
    const detail = entDetails[ent._id] || {};
    const isLaserZoneLike =
      detail.ltSlotCapacity != null ||
      detail.ltSlotRealCapacity != null ||
      detail.ltVestCountTotal != null;
    if (!isLaserZoneLike) return null;
    const total = detail.ltVestCountTotal;
    const defective = detail.ltVestCountDefective || 0;
    const available = total != null ? Math.max(0, total - defective) : null;
    const realCapacity = detail.ltSlotRealCapacity ?? detail.ltSlotCapacity;
    return (
      <Box
        sx={{
          mt: 2,
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: alpha(theme.palette.info.main, 0.06),
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={1}
        >
          <Box>
            <Typography variant='subtitle2'>Westen</Typography>
            <Typography variant='body2' color='text.secondary'>
              {total != null
                ? `Verfügbar: ${available} / Gesamt: ${total}`
                : 'Gesamtzahl noch nicht gesetzt'}
              {` · Defekt: ${defective}`}
            </Typography>
          </Box>
          <Stack direction='row' alignItems='center' spacing={0.5}>
            <Tooltip title='Defekte Weste entfernen'>
              <span>
                <IconButton
                  size='small'
                  disabled={!canManage || defective <= 0}
                  onClick={() => handleDefectiveChange(ent._id, -1)}
                >
                  <RemoveIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <Chip
              label={`${defective} defekt`}
              size='small'
              color={defective > 0 ? 'warning' : 'default'}
            />
            <Tooltip title='Defekte Weste melden'>
              <span>
                <IconButton
                  size='small'
                  disabled={!canManage || (total != null && defective >= total)}
                  onClick={() => handleDefectiveChange(ent._id, 1)}
                >
                  <AddIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            {canManage && (
              <Tooltip title='Westen-Einstellungen'>
                <IconButton size='small' onClick={() => setVestDialogEnt(ent)}>
                  <SettingsIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        {realCapacity != null && (
          <Typography variant='subtitle2' sx={{ mt: 1, color: theme.palette.info.main }}>
            Buchbare Kapazität: {realCapacity}
          </Typography>
        )}
      </Box>
    );
  };

  const vestDialogDetail = vestDialogEnt ? entDetails[vestDialogEnt._id] || {} : {};

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, color: 'text.primary' }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        flexWrap='wrap'
        gap={2}
      >
        <Box>
          <Typography variant='h5' fontWeight={700}>
            Ausfall-Board
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Entertainments und Räume als nicht bespielbar melden – Ticket, Eskalation und Blocker
            laufen automatisch.
          </Typography>
        </Box>
        <Stack direction='row' alignItems='center' spacing={1}>
          <FormControl size='small' sx={{ minWidth: 220 }}>
            <InputLabel id='outage-branch-filter-label'>Standort</InputLabel>
            <Select
              labelId='outage-branch-filter-label'
              label='Standort'
              value={branchFilter}
              onChange={e => setBranchFilter(e.target.value)}
            >
              <MenuItem value='all'>Alle Standorte</MenuItem>
              {branches.map(b => (
                <MenuItem key={b._id} value={b._id}>
                  {branchLabel(b)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title='Aktualisieren'>
            <IconButton onClick={() => loadAll()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && !overview ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack spacing={2} sx={{ mt: 3 }}>
            {visibleEntertainments.length === 0 && (
              <Alert severity='info'>Keine Entertainments für den gewählten Standort gefunden.</Alert>
            )}
            {visibleEntertainments.map(ent => {
              const entName = ent.entertainmentName || ent.longName || 'Entertainment';
              const branch = branches.find(b => b._id === ent.branch);
              const entOutage = entOutageByEnt[ent._id];
              const resources = entertainmentResourceIds(ent)
                .map(id => resourceById[id])
                .filter(Boolean)
                .filter(r => r.active !== false);
              return (
                <Paper
                  key={ent._id}
                  variant='outlined'
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    borderColor: entOutage ? theme.palette.error.main : theme.palette.divider,
                    backgroundColor: entOutage
                      ? alpha(theme.palette.error.main, 0.05)
                      : 'background.paper',
                    transition: 'border-color 0.3s ease, background-color 0.3s ease',
                  }}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    flexWrap='wrap'
                    gap={1}
                  >
                    <Box>
                      <Typography variant='h6' fontWeight={700}>
                        {entName}
                      </Typography>
                      {branch && (
                        <Typography variant='caption' color='text.secondary'>
                          {branchLabel(branch)}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction='row' alignItems='center' spacing={1}>
                      {entOutage ? (
                        <>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant='body2' color='error' fontWeight={600}>
                              Ausfall{entOutage.reason ? `: ${entOutage.reason}` : ''}
                            </Typography>
                            <Typography variant='caption' color='text.secondary'>
                              seit {dayjs(entOutage.startedAt).format(DATE_FORMAT)}
                            </Typography>
                          </Box>
                          {canManage && (
                            <Button
                              size='small'
                              variant='outlined'
                              color='error'
                              onClick={() =>
                                setEndTarget({ outageId: entOutage._id, name: entName })
                              }
                            >
                              Ausfall beenden
                            </Button>
                          )}
                        </>
                      ) : (
                        <Typography variant='body2' color='success.main' fontWeight={600}>
                          Bespielbar
                        </Typography>
                      )}
                      {availabilitySwitch(
                        entOutage,
                        () =>
                          openOutageDialog({
                            level: 'entertainment',
                            branchId: ent.branch,
                            entertainmentId: ent._id,
                            name: entName,
                          }),
                        () => entOutage && setEndTarget({ outageId: entOutage._id, name: entName })
                      )}
                    </Stack>
                  </Stack>

                  {resources.length > 0 && (
                    <Box
                      sx={{
                        mt: 2,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                        gap: 1.5,
                      }}
                    >
                      {resources.map(res => {
                        const resOutage = resOutageByEntRes[`${ent._id}_${res._id}`];
                        const effectiveOutage = resOutage || entOutage;
                        return (
                          <Paper
                            key={res._id}
                            variant='outlined'
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: effectiveOutage
                                ? alpha(theme.palette.error.main, 0.08)
                                : alpha(theme.palette.success.main, 0.04),
                              borderColor: effectiveOutage
                                ? alpha(theme.palette.error.main, 0.5)
                                : theme.palette.divider,
                              transition: 'background-color 0.3s ease, border-color 0.3s ease',
                            }}
                          >
                            <Stack
                              direction='row'
                              alignItems='center'
                              justifyContent='space-between'
                            >
                              <Stack
                                direction='row'
                                alignItems='center'
                                spacing={1}
                                sx={{ minWidth: 0 }}
                              >
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    backgroundColor: effectiveOutage
                                      ? theme.palette.error.main
                                      : theme.palette.success.main,
                                  }}
                                />
                                <Typography variant='body2' fontWeight={600} noWrap>
                                  {res.name}
                                </Typography>
                              </Stack>
                              {availabilitySwitch(
                                resOutage,
                                () =>
                                  openOutageDialog({
                                    level: 'resource',
                                    branchId: ent.branch,
                                    entertainmentId: ent._id,
                                    resourceId: res._id,
                                    name: `${entName} – ${res.name}`,
                                  }),
                                () =>
                                  resOutage &&
                                  setEndTarget({ outageId: resOutage._id, name: res.name }),
                                true
                              )}
                            </Stack>
                            {resOutage && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant='caption' color='error' display='block'>
                                  {resOutage.reason || 'Ausfall'} · seit{' '}
                                  {dayjs(resOutage.startedAt).format(DATE_FORMAT)}
                                </Typography>
                                {canManage && (
                                  <Button
                                    size='small'
                                    color='error'
                                    onClick={() =>
                                      setEndTarget({ outageId: resOutage._id, name: res.name })
                                    }
                                  >
                                    Beenden
                                  </Button>
                                )}
                              </Box>
                            )}
                          </Paper>
                        );
                      })}
                    </Box>
                  )}

                  {renderVestBox(ent)}
                </Paper>
              );
            })}
          </Stack>

          <Typography variant='h6' fontWeight={700} sx={{ mt: 4, mb: 1 }}>
            Aktive & kürzlich beendete Ausfälle
          </Typography>
          <Paper variant='outlined' sx={{ borderRadius: 2, overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Ziel</TableCell>
                  <TableCell>Ebene</TableCell>
                  <TableCell>Grund</TableCell>
                  <TableCell>Gemeldet</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ticket</TableCell>
                  <TableCell align='right' />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedOutages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant='body2' color='text.secondary'>
                        Keine Ausfälle vorhanden.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {sortedOutages.map(o => {
                  const targetName =
                    o.resourceId?.name ||
                    o.entertainmentId?.entertainmentName ||
                    o.entertainmentId?.longName ||
                    branchLabel(o.branchId) ||
                    '—';
                  const priority = o.issueId ? ISSUE_PRIORITY_BY_KEY[o.issueId.priority] : null;
                  return (
                    <TableRow key={o._id} hover>
                      <TableCell>
                        <Typography variant='body2' fontWeight={600}>
                          {targetName}
                        </Typography>
                        {o.branchId && o.resourceId && (
                          <Typography variant='caption' color='text.secondary'>
                            {branchLabel(o.branchId)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          variant='outlined'
                          label={o.level === 'entertainment' ? 'Entertainment' : 'Ressource'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{o.reason || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{o.reportedByUsername || '—'}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          seit {dayjs(o.startedAt).format(DATE_FORMAT)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {o.status === 'active' ? (
                          <>
                            <Chip size='small' color='error' label='Aktiv' />
                            {o.blockedUntil && (
                              <Typography variant='caption' color='text.secondary' display='block'>
                                geblockt bis {dayjs(o.blockedUntil).format(DATE_FORMAT)}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <>
                            <Chip size='small' label='Beendet' />
                            {o.endedAt && (
                              <Typography variant='caption' color='text.secondary' display='block'>
                                {dayjs(o.endedAt).format(DATE_FORMAT)}
                              </Typography>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        {o.issueId ? (
                          <Chip
                            size='small'
                            clickable
                            onClick={() => navigate(`/issues?issueId=${o.issueId._id}`)}
                            label={`${priority?.shortLabel || o.issueId.priority} · ${o.issueId.title}`}
                            sx={
                              priority
                                ? {
                                    backgroundColor: alpha(priority.color, 0.15),
                                    color: priority.color,
                                    fontWeight: 600,
                                  }
                                : undefined
                            }
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align='right'>
                        {o.status === 'active' && canManage && (
                          <Button
                            size='small'
                            color='error'
                            onClick={() => setEndTarget({ outageId: o._id, name: targetName })}
                          >
                            Beenden
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* Create-outage dialog (shared for entertainment and resource level). */}
      <Dialog
        open={!!outageTarget}
        onClose={() => !busy && setOutageTarget(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReportProblemIcon color='error' /> Ausfall melden
        </DialogTitle>
        <DialogContent>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 2 }}>
            {outageTarget?.name}
          </Typography>
          <Alert severity='warning' sx={{ mb: 2 }}>
            Es wird automatisch ein P1-Ticket eröffnet, die Eskalationskette gestartet und der
            Zeitraum geblockt.
          </Alert>
          <TextField
            inputRef={reasonRef}
            label='Grund'
            multiline
            minRows={3}
            fullWidth
            autoFocus
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOutageTarget(null)} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleCreateOutage}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Ausfall melden
          </Button>
        </DialogActions>
      </Dialog>

      {/* End-outage confirmation dialog. */}
      <Dialog
        open={!!endTarget}
        onClose={() => !busy && setEndTarget(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Ausfall beenden?</DialogTitle>
        <DialogContent>
          <Typography variant='body2'>
            Der Ausfall für <strong>{endTarget?.name}</strong> wird beendet. Der Zeitraum-Blocker
            wird entfernt und das zugehörige Ticket gelöst.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEndTarget(null)} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='success'
            onClick={handleEndOutage}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Ausfall beenden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vest settings dialog (total count / real capacity). */}
      <Dialog
        open={!!vestDialogEnt}
        onClose={() => !busy && setVestDialogEnt(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Westen-Einstellungen</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {vestDialogEnt?.entertainmentName || vestDialogEnt?.longName}
          </Typography>
          <TextField
            inputRef={vestTotalRef}
            label='Gesamtzahl Westen'
            type='number'
            fullWidth
            defaultValue={vestDialogDetail.ltVestCountTotal ?? ''}
            inputProps={{ min: 0 }}
            sx={{ mb: 2 }}
          />
          {vestDialogDetail.ltVestCountTotal == null && (
            <TextField
              inputRef={vestRealCapacityRef}
              label='Kapazität ohne Defekte'
              type='number'
              fullWidth
              defaultValue={vestDialogDetail.ltSlotCapacity ?? ''}
              inputProps={{ min: 0 }}
              helperText='Solange keine Gesamtzahl gesetzt ist: Grundkapazität ohne Defekte. Defekte Westen werden davon abgezogen.'
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setVestDialogEnt(null)} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleVestSettingsSave}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color='inherit' /> : null}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
